<template>
  <div class="search-shell relative mx-auto max-w-5xl pb-4"> 


    <div
      v-if="!isCompact"
      ref="searchAreaRef"
      :class="pillClass"
    >
      <div
        class="grid flex-1 items-center"
        :class="showExtendedFilters ? 'grid-cols-2' : 'grid-cols-1'"
      >
        <button class="relative pill-item text-left transition focus:outline-none" @click.stop="togglePanel('location')">
          <p class="label">Where</p>
          <p class="value">{{ locationText || 'Search destinations' }}</p>
          <div v-if="activePanel === 'location'" class="panel">
            <div class="space-y-3">
              <div class="rounded-2xl border border-gray-200 px-3 py-2 text-sm shadow-sm">
                <input
                  v-model="locationQuery"
                  class="w-full border-none bg-transparent text-sm text-ink placeholder:text-muted focus:outline-none"
                  placeholder="Type a city or area"
                  @input="filterLocations"
                />
              </div>
              <div class="max-h-72 overflow-y-auto space-y-2 text-sm text-ink">
                <button
                  class="flex w-full items-center gap-3 rounded-2xl px-3 py-2 text-left hover:bg-gray-50"
                  @click.stop="useCurrentLocation"
                >
                  <span class="flex h-10 w-10 items-center justify-center rounded-2xl bg-amber-50 text-xl">📍</span>
                  <div>
                    <p class="text-sm font-semibold text-ink">Use current location</p>
                    <p class="text-xs text-muted">Detect and search nearby</p>
                  </div>
                </button>
                <button
                  v-for="item in filteredLocations"
                  :key="item.label"
                  class="flex w-full items-start gap-3 rounded-2xl px-3 py-2 text-left hover:bg-gray-50"
                  @click.stop="selectLocation(item.label, item.placeId)"
                >
                  <span class="flex h-10 w-10 items-center justify-center rounded-2xl bg-blue-50 text-xl">📍</span>
                  <div>
                    <p class="text-sm font-semibold text-ink">{{ item.label }}</p>
                    <p class="text-xs text-muted">{{ item.meta }}</p>
                  </div>
                </button>
                <p v-if="!filteredLocations.length" class="rounded-xl border border-dashed border-gray-200 px-3 py-2 text-xs text-muted">Type at least 3 characters to search.</p>
              </div>
            </div>
          </div>
        </button>

        <template v-if="showExtendedFilters">
          <div class="relative grid flex-1 items-center text-left gap-4">
            <button class="pill-item divider text-left transition focus:outline-none" @click.stop="togglePanel('guests')">
              <p class="label">Guests</p>
              <p class="value">{{ guestRangeSummary }}</p>
            </button>
            <div v-if="activePanel === 'guests'" class="panel w-64">
              <p class="mb-2 text-sm font-semibold text-ink">No. of Guests</p>
              <div class="space-y-2 text-sm text-ink">
                <label
                  v-for="range in guestRanges"
                  :key="range.value"
                  class="flex items-center gap-2 rounded-xl px-2 py-1 hover:bg-gray-50"
                >
                  <input
                    type="checkbox"
                    class="h-4 w-4 rounded border-gray-300 text-brand focus:ring-brand"
                    :checked="selectedGuestRange === range.value"
                    @change.stop="selectedGuestRange = selectedGuestRange === range.value ? '' : range.value"
                  >
                  <span>{{ range.label }}</span>
                </label>
              </div>
            </div>
          </div>
        </template>
      </div>
      <button class="search-btn" @click="applySearch" aria-label="Search">
        <svg class="h-5 w-5" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" d="M21 21l-5.2-5.2M10 16a6 6 0 110-12 6 6 0 010 12z" />
        </svg>
      </button>
    </div>

    <div
      v-else
      ref="searchAreaRef"
      class="search-pill mini"
      @click.capture="emit('expand')"
    >
      <div
        class="grid flex-1 items-center text-sm font-semibold text-ink"
        :class="showExtendedFilters ? 'grid-cols-2' : 'grid-cols-1'"
      >
        <button class="relative pill-item mini text-left focus:outline-none" @click.stop="togglePanel('location')">
          <span class="text-muted">{{ locationText || 'Where to?' }}</span>
          <div v-if="activePanel === 'location'" class="panel">
            <div class="space-y-3">
              <div class="rounded-2xl border border-gray-200 px-3 py-2 text-sm shadow-sm">
                <input
                  v-model="locationQuery"
                  class="w-full border-none bg-transparent text-sm text-ink placeholder:text-muted focus:outline-none"
                  placeholder="Type a city or area"
                  @input="filterLocations"
                />
              </div>
              <div class="max-h-72 overflow-y-auto space-y-2 text-sm text-ink">
                <button
                  class="flex w-full items-center gap-3 rounded-2xl px-3 py-2 text-left hover:bg-gray-50"
                  @click.stop="useCurrentLocation"
                >
                  <span class="flex h-10 w-10 items-center justify-center rounded-2xl bg-amber-50 text-xl">📍</span>
                  <div>
                    <p class="text-sm font-semibold text-ink">Use current location</p>
                    <p class="text-xs text-muted">Detect and search nearby</p>
                  </div>
                </button>
                <button
                  v-for="item in filteredLocations"
                  :key="item.label"
                  class="flex w-full items-start gap-3 rounded-2xl px-3 py-2 text-left hover:bg-gray-50"
                  @click.stop="selectLocation(item.label, item.placeId)"
                >
                  <span class="flex h-10 w-10 items-center justify-center rounded-2xl bg-blue-50 text-xl">📍</span>
                  <div>
                    <p class="text-sm font-semibold text-ink">{{ item.label }}</p>
                    <p class="text-xs text-muted">{{ item.meta }}</p>
                  </div>
                </button>
                <p v-if="!filteredLocations.length" class="rounded-xl border border-dashed border-gray-200 px-3 py-2 text-xs text-muted">Type at least 3 characters to search.</p>
              </div>
            </div>
          </div>
        </button>

        <template v-if="showExtendedFilters">
          <button class="relative pill-item mini text-left focus:outline-none" @click.stop="togglePanel('guests')">
            <span class="text-muted">{{ guestRangeSummary }}</span>
            <div v-if="activePanel === 'guests'" class="panel w-64">
              <p class="mb-2 text-sm font-semibold text-ink">No. of Guests</p>
              <div class="space-y-2 text-sm text-ink">
                <label
                  v-for="range in guestRanges"
                  :key="range.value"
                  class="flex items-center gap-2 rounded-xl px-2 py-1 hover:bg-gray-50"
                >
                  <input
                    type="checkbox"
                    class="h-4 w-4 rounded border-gray-300 text-brand focus:ring-brand"
                    :checked="selectedGuestRange === range.value"
                    @change.stop="selectedGuestRange = selectedGuestRange === range.value ? '' : range.value"
                  >
                  <span>{{ range.label }}</span>
                </label>
              </div>
            </div>
          </button>
        </template>
      </div>
      <button class="search-btn mini" @click.stop="applySearch">
        <svg class="h-4 w-4" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" d="M21 21l-5.2-5.2M10 16a6 6 0 110-12 6 6 0 010 12z" />
        </svg> 
      </button>
    </div>
  </div>
</template>

<script setup>
/* global defineProps, defineEmits */
import { computed, onMounted, onUnmounted, ref } from 'vue'
import { useRouter } from 'vue-router'
import { useSearchStore } from '@/stores/search'

const props = defineProps({
  compact: {
    type: Boolean,
    default: false
  },
  context: {
    type: String,
    default: 'vendors'
  }
})

const emit = defineEmits(['expand'])

// Set your Google Places API key here (no .env needed). Leave blank to use fallback suggestions only.
const GOOGLE_API_KEY = 'AIzaSyDOAEexenU_AutS0ULOGzNa3HQZHpikt4A'

const searchStore = useSearchStore()
const router = useRouter()

const searchAreaRef = ref(null)
const activePanel = ref('')
const locationText = ref(searchStore.locationText || '')
const locationQuery = ref('')
const lat = ref(searchStore.lat)
const lng = ref(searchStore.lng)
const selectedPlaceId = ref('')
const googleReady = ref(false)
const scriptLoading = ref(false)
const autocompleteService = ref(null)
const placesService = ref(null)

const filteredLocations = ref([{ label: 'Use current location to start', meta: '', placeId: null }])

const isCompact = computed(() => props.compact)
const searchContext = computed(() => {
  const val = (props.context || '').toString().toLowerCase()
  if (val.includes('venue')) return 'venues'
  if (val.includes('vendor')) return 'vendors'
  if (val.includes('home')) return 'home'
  return 'vendors'
})
const showExtendedFilters = computed(() => searchContext.value === 'venues')
const guestRanges = [
  { label: '< 100', value: '<100' },
  { label: '100-250', value: '100-250' },
  { label: '250-500', value: '250-500' },
  { label: '500-1000', value: '500-1000' },
  { label: '> 1000', value: '>1000' }
]
const selectedGuestRange = ref('')
const guestRangeSummary = computed(() => selectedGuestRange.value ? `Guests: ${selectedGuestRange.value}` : 'Add guests')
const pillClass = computed(() => {
  if (searchContext.value === 'vendors') return 'search-pill search-pill-wide'
  if (searchContext.value === 'venues') return 'search-pill'
  return 'search-pill'
})

const guests = ref({ ...searchStore.guests })

const togglePanel = (panel) => {
  if (panel === 'location') {
    activePanel.value = 'location'
    ensureGooglePlaces()
    return
  }
  activePanel.value = activePanel.value === panel ? '' : panel
}

const ensureGooglePlaces = () => {
  if (!GOOGLE_API_KEY) return
  if (googleReady.value || scriptLoading.value) return
  scriptLoading.value = true
  const script = document.createElement('script')
  script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_API_KEY}&libraries=places`
  script.async = true
  script.onload = () => {
    googleReady.value = true
    autocompleteService.value = new window.google.maps.places.AutocompleteService()
    placesService.value = new window.google.maps.places.PlacesService(document.createElement('div'))
  }
  script.onerror = () => {
    scriptLoading.value = false
  }
  document.head.appendChild(script)
}

const filterLocations = () => {
  const q = locationQuery.value.trim()
  if (googleReady.value && autocompleteService.value && q.length > 2) {
    autocompleteService.value.getPlacePredictions({ input: q }, (predictions) => {
      if (!predictions) return
      filteredLocations.value = predictions.map((p) => ({ label: p.description, placeId: p.place_id, meta: '' }))
    })
    return
  }
  filteredLocations.value = []
}

const geocodePlace = (placeId) => {
  if (!placesService.value || !placeId) return Promise.resolve({ lat: null, lng: null })
  return new Promise((resolve) => {
    placesService.value.getDetails({ placeId, fields: ['geometry.location'] }, (res, status) => {
      if (status === 'OK' && res?.geometry?.location) {
        resolve({ lat: res.geometry.location.lat(), lng: res.geometry.location.lng() })
      } else {
        resolve({ lat: null, lng: null })
      }
    })
  })
}

const selectLocation = async (label, placeId) => {
  locationText.value = label
  selectedPlaceId.value = placeId || ''
  if (placeId) {
    const coords = await geocodePlace(placeId)
    lat.value = coords.lat
    lng.value = coords.lng
  }
  searchStore.setLocation({ text: locationText.value, lat: lat.value, lng: lng.value })
  activePanel.value = 'location'
}

const useCurrentLocation = () => {
  if (!navigator.geolocation) {
    locationText.value = 'Current location (permission needed)'
    return
  }
  navigator.geolocation.getCurrentPosition(
    (pos) => {
      lat.value = pos.coords.latitude
      lng.value = pos.coords.longitude
      locationText.value = 'Current location'
      selectedPlaceId.value = ''
      searchStore.setLocation({ text: locationText.value, lat: lat.value, lng: lng.value })
    },
    () => {
      locationText.value = 'Current location (denied)'
    }
  )
}

const slugify = (val) => {
  return (val || 'all').toString().trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '') || 'all'
}

const applySearch = () => {
  searchStore.setLocation({ text: locationText.value, lat: lat.value, lng: lng.value })
  if (showExtendedFilters.value) {
    searchStore.setGuests({
      adults: selectedGuestRange.value || guests.value.adults,
      children: 0,
      infants: 0,
      pets: 0
    })
  } else {
    searchStore.setGuests(guests.value)
  }
  activePanel.value = ''

  const totalGuests = showExtendedFilters.value
    ? undefined
    : guests.value.adults + guests.value.children + guests.value.infants + guests.value.pets
  const slug = slugify(locationText.value)
  const basePath = searchContext.value === 'venues' ? '/venues' : '/vendors'
  const searchType = searchContext.value === 'home' ? 'HOMEPAGE' : searchContext.value.toUpperCase()
  const path = `${basePath}/${slug}`
  const query = {
    place_id: selectedPlaceId.value || undefined,
    lat: lat.value || undefined,
    lng: lng.value || undefined,
    adults: showExtendedFilters.value ? undefined : guests.value.adults || undefined,
    children: showExtendedFilters.value ? undefined : guests.value.children || undefined,
    infants: showExtendedFilters.value ? undefined : guests.value.infants || undefined,
    pets: showExtendedFilters.value ? undefined : guests.value.pets || undefined,
    guests: totalGuests || undefined,
    guests_range: showExtendedFilters.value ? selectedGuestRange.value || undefined : undefined,
    search_type: searchType
  }
  const qs = new URLSearchParams()
  Object.entries(query).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== '') qs.append(k, v)
  })
  // eslint-disable-next-line no-console
  console.log(`GET ${path}?${qs.toString()}`)
  router.push({ path, query })
}

const handleClickOutside = (event) => {
  if (!activePanel.value) return
  const target = event.target
  if (searchAreaRef.value && searchAreaRef.value.contains(target)) return
  activePanel.value = ''
}

const handleEsc = (event) => {
  if (event.key === 'Escape') {
    activePanel.value = ''
  }
}

onMounted(() => {
  window.addEventListener('click', handleClickOutside)
  window.addEventListener('keydown', handleEsc)
})

onUnmounted(() => {
  window.removeEventListener('click', handleClickOutside)
  window.removeEventListener('keydown', handleEsc)
})
</script>

<style scoped>
.panel {
  position: absolute;
  left: 0;
  top: calc(100% + 12px);
  z-index: 2000;
  width: min(480px, 80vw);
  border-radius: 18px;
  background: white;
  padding: 14px;
  box-shadow: 0 20px 60px rgba(0,0,0,0.15);
  border: 1px solid #e2e8f0;
}

.search-shell {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
}

.search-modes {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 36px;
  margin-bottom: 2px;
}

.mode {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 10px;
  border: none;
  background: transparent;
  font-weight: 700;
  color: #374151;
  border-bottom: 2px solid transparent;
  padding-bottom: 6px;
}

.mode.active {
  color: #111827;
  border-color: #111;
}

.emoji {
  font-size: 18px;
}

.pill-badge {
  padding: 2px 8px;
  border-radius: 999px;
  background: #0f172a;
  color: #fff;
  font-size: 10px;
  font-weight: 700;
}

.search-pill {
  display: flex;
  align-items: center;
  gap: 26px;
  background: #fff;
  border-radius: 999px;
  padding: 12px 16px 12px 20px;
  box-shadow: 0 16px 36px rgba(0,0,0,0.09);
  border: 1px solid #e5e7eb;
  transition: all 0.2s ease;
  width: 100%;
}

.search-pill-wide {
  max-width: 1100px;
}

.pill-item {
  padding: 0px 10px;
  border-radius: 999px;
}

.label {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #4b5563;
}

.value {
  font-size: 14px;
  font-weight: 600;
  color: #111827;
}

.divider {
  position: relative;
}

.divider::before {
  content: '';
  position: absolute;
  left: 0;
  top: 8px;
  bottom: 8px;
  width: 1px;
  background: #e5e7eb;
}

.search-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 48px;
  width: 48px;
  border-radius: 50%;
  background: #fe8317;
  color: #fff;
  font-weight: 700;
  border: none; 
  transition: transform 0.15s ease, box-shadow 0.15s ease;
}

.search-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 12px 24px rgba(255, 55, 95, 0.45);
}

.search-pill.mini {
  max-width: 520px;
  padding: 8px 10px;
  background: #f8f9fb;
  border: 1px solid #e5e7eb;
  box-shadow: 0 10px 24px rgba(0,0,0,0.06);
  margin: 0 auto;
}

.pill-item.mini {
  padding: 6px 12px;
}

.search-btn.mini {
  height: 40px;
  width: 40px;
  padding: 0 12px;
  box-shadow: 0 10px 20px rgba(236,34,99,0.35);
}
</style>
